import { DEFAULT_EMAIL_TEMPLATES, DEFAULT_INSURANCE_TEMPLATE, DEFAULT_INSURANCE_TEXT, DEFAULT_OVERDUE_EMAIL, DEFAULT_OVERDUE_TEXT, type EmailTemplate, DEFAULT_TEXT_TEMPLATES, type InsuranceTemplate, EMAIL_DOC_KINDS, EMAIL_DOC_LABELS, EMAIL_PLACEHOLDERS, type EmailTemplates as Templates, type TextTemplates } from "@nmt/shared";
import { useEffect, useState } from "react";
import { errorMessage } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, TextAreaField } from "../../components/ui";
import { useSettings } from "./useSettings";

/** Settings → Email templates: what "Email it" starts with for proposals, change orders, and invoices. */
export function EmailTemplates() {
  const { query, save } = useSettings();
  const [t, setT] = useState<Templates | null>(null);
  const [tx, setTx] = useState<TextTemplates | null>(null);
  const [ins, setIns] = useState<InsuranceTemplate | null>(null);
  const [od, setOd] = useState<EmailTemplate | null>(null);
  const [odText, setOdText] = useState("");
  const [insOn, setInsOn] = useState(true);
  const [insText, setInsText] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (query.data && !t) setT(query.data.settings["email.templates"]);
    if (query.data && !tx) setTx(query.data.settings["text.templates"]);
    if (query.data && !od) {
      setOd(query.data.settings["email.overdueTemplate"]);
      setOdText(query.data.settings["text.overdueTemplate"]);
    }
    if (query.data && !ins) {
      setIns(query.data.settings["insurance.template"]);
      setInsOn(query.data.settings["insurance.reminders"]);
      setInsText(query.data.settings["insurance.textTemplate"]);
    }
  }, [query.data, t, tx, ins, od]);
  if (query.isLoading || !t || !tx || !ins || !od) return <Loading />;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Email and text templates" />
      <p className="text-sm text-ink-soft">
        What <b>Email it</b> fills in. The rep can change the wording each time before sending. The link goes in automatically as a button below the message. You can use{" "}
        {EMAIL_PLACEHOLDERS.map((p, i) => (
          <span key={p}>
            <code className="rounded bg-mist px-1">{`{${p}}`}</code>
            {i < EMAIL_PLACEHOLDERS.length - 1 ? ", " : "."}
          </span>
        ))}
      </p>
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {notice && <Alert tone="ok">{notice}</Alert>}
      {EMAIL_DOC_KINDS.map((k) => (
        <Panel key={k}>
          <PanelHeader
            title={EMAIL_DOC_LABELS[k]}
            action={
              <Button variant="quiet" className="px-3 py-1.5" onClick={() => (setT({ ...t, [k]: DEFAULT_EMAIL_TEMPLATES[k] }), setTx({ ...tx, [k]: DEFAULT_TEXT_TEMPLATES[k] }))}>
                Back to the original
              </Button>
            }
          />
          <div className="flex flex-col gap-3 px-5 py-4">
            <Field label="Subject" value={t[k].subject} onChange={(e) => setT({ ...t, [k]: { ...t[k], subject: e.target.value } })} />
            <TextAreaField label="Message" rows={9} value={t[k].body} onChange={(e) => setT({ ...t, [k]: { ...t[k], body: e.target.value } })} />
            <Field label="Button" value={t[k].button} onChange={(e) => setT({ ...t, [k]: { ...t[k], button: e.target.value } })} className="sm:max-w-xs" />
            <TextAreaField label="Text message (the link is added at the end)" rows={3} value={tx[k]} onChange={(e) => setTx({ ...tx, [k]: e.target.value })} />
          </div>
        </Panel>
      ))}
      <Panel>
        <PanelHeader
          title="Past-due invoice reminder"
          description="What Send reminder on the Past due card starts with. Also {balance} (what's still owed) and {due_date}."
          action={
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => (setOd(DEFAULT_OVERDUE_EMAIL), setOdText(DEFAULT_OVERDUE_TEXT))}>
              Back to the original
            </Button>
          }
        />
        <div className="flex flex-col gap-3 px-5 py-4">
          <Field label="Subject" value={od.subject} onChange={(e) => setOd({ ...od, subject: e.target.value })} />
          <TextAreaField label="Message" rows={9} value={od.body} onChange={(e) => setOd({ ...od, body: e.target.value })} />
          <Field label="Button" value={od.button} onChange={(e) => setOd({ ...od, button: e.target.value })} className="sm:max-w-xs" />
          <TextAreaField label="Text message (the link is added at the end)" rows={3} value={odText} onChange={(e) => setOdText(e.target.value)} />
        </div>
      </Panel>
      <Panel>
        <PanelHeader
          title="Sub insurance reminders"
          description={`Emailed to subs ${query.data!.settings["compliance.reminderDays"]} days before their workers' comp or liability expires, and again when it does (once each), from the office address with an "Upload your new certificate" button. You can use {sub_name}, {policy}, {expires}, {expires_phrase}, {company}, {company_phone}.`}
          action={
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => (setIns(DEFAULT_INSURANCE_TEMPLATE), setInsText(DEFAULT_INSURANCE_TEXT))}>
              Back to the original
            </Button>
          }
        />
        <div className="flex flex-col gap-3 px-5 py-4">
          <Checkbox label="Email subs automatically" description="Off: nothing is sent on its own; you can still email a reminder from the insurance card." checked={insOn} onChange={setInsOn} />
          <Field label="Subject" value={ins.subject} onChange={(e) => setIns({ ...ins, subject: e.target.value })} />
          <TextAreaField label="Message" rows={9} value={ins.body} onChange={(e) => setIns({ ...ins, body: e.target.value })} />
          <TextAreaField label="Text message, for Remind… by text (the upload link is added at the end)" rows={3} value={insText} onChange={(e) => setInsText(e.target.value)} />
        </div>
      </Panel>
      <div>
        <Button variant="primary" busy={save.isPending} onClick={() => (setNotice(null), save.mutate({ "email.templates": t, "text.templates": tx, "insurance.template": ins, "insurance.reminders": insOn, "insurance.textTemplate": insText, "email.overdueTemplate": od, "text.overdueTemplate": odText }, { onSuccess: () => setNotice("Saved.") }))}>
          Save templates
        </Button>
      </div>
    </div>
  );
}
